import React, { useEffect, useState } from 'react';
import { useTranslation } from '@app/core/i18n';

import SearchIcon from '@ast/magma/components/icon/icons/Search';
import CloseIcon from '@ast/magma/components/icon/icons/Times';

import { Button } from '@ast/magma/components/button';
import { Input } from '@ast/magma/components/input';
import { InputProps } from '@ast/magma/components/input/input';

import { useDebouncedFunction } from '@app/common/utils/hooks/useDebouncedFunction';

import styles from './SearchField.pcss';

const DEFAULT_INPUT_DELAY = 500;
const DEFAULT_MAX_LENGTH = 200;
const DEFAULT_VALUE = '';

type FieldProps = {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  onClear: () => void;
} & Omit<InputProps, 'label' | 'value' | 'onChange'>;

const SearchFieldBase: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  onClear,
  ...props
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(value || DEFAULT_VALUE);

  useEffect(() => {
    setInputValue(value || DEFAULT_VALUE);
  }, [value]);

  return (
    <div role="search">
      <Input
        maxLength={DEFAULT_MAX_LENGTH}
        data-stable-name="SearchInput"
        placeholder={label || t('search-field.label|Search field label', 'Search')}
        className={styles.root}
        onChange={(event) => {
          const searchText = event.target.value;

          onChange(searchText);
          setInputValue(searchText);
        }}
        value={inputValue}
        leadingChild={<SearchIcon />}
        trailingChild={inputValue ? (
          <Button
            data-stable-name="ClearButton"
            look="icon"
            icon={<CloseIcon />}
            onClick={() => {
              onClear();
              setInputValue(DEFAULT_VALUE);
            }}
            aria-label={t(
              'search-field.clear-button|Clear search button label',
              'Clear search',
            )}
          />
        ) : undefined}
        {...props}
      />
    </div>
  );
};

export const SearchField = React.memo<Omit<FieldProps, 'onClear'>>(({ onChange, ...props }) => {
  const handleChange = useDebouncedFunction((searchText) => {
    onChange(searchText);
  }, DEFAULT_INPUT_DELAY);

  const handleClear = () => {
    onChange(DEFAULT_VALUE);
  };

  return (
    <SearchFieldBase
      {...props}
      onChange={handleChange}
      onClear={handleClear}
    />
  );
});
